import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Search,
  AlertTriangle,
  CheckCircle,
  Users,
  Trash2,
  Shield,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";

/**
 * SupersedeAppsModal - Modal for managing previous app versions during deployment
 * 
 * When deploying a new version:
 * - TEST deployment: Store selected apps, exclusion happens after test group is created
 * - LIVE deployment: Delete assignments from old apps entirely
 */
export default function SupersedeAppsModal({
  open,
  onOpenChange,
  packageId,
  packageName,
  deploymentType = "test", // "test" or "live"
  onConfirm,
  getAuthHeaders,
  API,
}) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [matches, setMatches] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [selectedApps, setSelectedApps] = useState(new Set());
  const [showAllApps, setShowAllApps] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);

  // Fetch matching apps when modal opens
  useEffect(() => {
    if (open && packageId) {
      fetchSupersedeApps();
    }
  }, [open, packageId]);

  const fetchSupersedeApps = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const headers = await getAuthHeaders();
      console.log("Fetching supersede apps for package:", packageId);
      const response = await axios.get(
        `${API}/packages/${packageId}/search-supersede-apps`,
        { headers }
      );
      
      console.log("Supersede apps response:", response.data);
      const data = response.data;
      setMatches(data.matches || []);
      setAllApps(data.all_apps || []);
      
      // Tick only the previous ALICE version of this app. Ticking every
      // name match (score 60+ with assignments) linked several old versions,
      // or unrelated apps, and each one counts towards Intune's 11-app
      // supersedence graph. Anything else can still be ticked by hand.
      const autoSelected = new Set();
      (data.matches || []).forEach(app => {
        if (app.is_previous_alice_version) {
          autoSelected.add(app.id);
        }
      });
      setSelectedApps(autoSelected);
      
    } catch (error) {
      console.error("Failed to fetch supersede apps:", error);
      console.error("Error response:", error.response?.data);
      setErrorMessage(error.response?.data?.detail || error.message || "Failed to search for existing apps");
      toast.error("Failed to search for existing apps");
    } finally {
      setLoading(false);
    }
  };

  const toggleAppSelection = (appId) => {
    setSelectedApps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appId)) {
        newSet.delete(appId);
      } else {
        newSet.add(appId);
      }
      return newSet;
    });
  };

  const handleConfirm = async () => {
    // If no apps selected, just proceed
    if (selectedApps.size === 0) {
      onConfirm?.();
      return;
    }

    // For test deployment, we'll store the selected apps and handle exclusion during deployment
    // (after the test group is created)
    if (deploymentType === "test") {
      // Pass selected apps to the deployment flow - exclusion will happen after test group creation
      onConfirm?.(Array.from(selectedApps));
      return;
    }

    // For live deployment, process immediately (delete assignments)
    setProcessing(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API}/packages/${packageId}/manage-supersede-apps`,
        {
          selected_app_ids: Array.from(selectedApps),
          action: deploymentType === "test" ? "exclude_test" : "delete_assignments",
          test_group_id: deploymentType === "test" ? selectedTestGroup : null,
        },
        { headers }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onConfirm?.();
      } else {
        toast.warning(response.data.message);
        // Still proceed even with partial success
        onConfirm?.();
      }
    } catch (error) {
      console.error("Failed to manage supersede apps:", error);
      toast.error("Failed to manage previous apps");
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = () => {
    onConfirm?.();
  };

  // Filter all apps based on search
  const filteredAllApps = allApps.filter(app => 
    app.displayName?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const getMatchScoreBadge = (score) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800 text-xs">Exact Match</Badge>;
    if (score >= 60) return <Badge className="bg-blue-100 text-blue-800 text-xs">Strong Match</Badge>;
    if (score >= 40) return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Partial Match</Badge>;
    return <Badge className="bg-gray-100 text-gray-800 text-xs">Weak Match</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Manage Previous App Versions
          </DialogTitle>
          <DialogDescription>
            {deploymentType === "test" ? (
              <>
                Select existing apps in Intune that the new version will supersede (no auto-uninstall).
                This ensures testers receive the new version of <strong>{packageName}</strong>.
              </>
            ) : (
              <>
                Select existing apps to remove assignments from when going live with <strong>{packageName}</strong>.
                This prevents duplicate deployments.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4 py-4">
          {/* Info Banner */}
          <div className={`p-3 rounded-lg border ${
            deploymentType === "test" 
              ? "bg-blue-50 border-blue-200" 
              : "bg-amber-50 border-amber-200"
          }`}>
            <div className="flex items-start gap-2">
              <Info className={`h-4 w-4 mt-0.5 ${
                deploymentType === "test" ? "text-blue-600" : "text-amber-600"
              }`} />
              <div className="text-sm">
                {deploymentType === "test" ? (
                  <p className="text-blue-800">
                    <strong>Test Deployment:</strong> Selected apps will have the test group (created during deployment) added as an exclusion. 
                    Live users will still receive the old version until you go live.
                  </p>
                ) : (
                  <p className="text-amber-800">
                    <strong>Live Deployment:</strong> All assignments will be removed from selected apps.
                    Users will receive the new version instead.
                  </p>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Searching Intune apps...</span>
            </div>
          ) : errorMessage ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
              <p className="text-muted-foreground mb-2">Failed to load Intune apps</p>
              <p className="text-xs text-red-500 mb-4">{errorMessage}</p>
              <Button variant="outline" size="sm" onClick={fetchSupersedeApps}>
                Retry
              </Button>
            </div>
          ) : (
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4">
                {/* Auto-detected matches */}
                {matches.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Detected Matches ({matches.length})
                    </Label>
                    <div className="space-y-2">
                      {matches.map(app => (
                        <div
                          key={app.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedApps.has(app.id)
                              ? "border-primary bg-primary/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => toggleAppSelection(app.id)}
                          data-testid={`supersede-app-${app.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedApps.has(app.id)}
                              onCheckedChange={() => toggleAppSelection(app.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{app.displayName}</span>
                                {app.displayVersion && (
                                  <Badge variant="secondary" className="text-xs font-mono">
                                    v{app.displayVersion}
                                  </Badge>
                                )}
                                {app.is_previous_alice_version
                                  ? <Badge className="bg-green-100 text-green-800 text-xs">Previous ALICE version</Badge>
                                  : getMatchScoreBadge(app.match_score)}
                                {app.has_assignments && (
                                  <Badge variant="outline" className="text-xs">
                                    <Users className="h-3 w-3 mr-1" />
                                    {app.assignment_count} assignment(s)
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {app.publisher || "Unknown publisher"}
                                {app.createdDateTime && ` • Created ${new Date(app.createdDateTime).toLocaleDateString()}`}
                              </p>
                              {!app.has_assignments && (
                                <p className="text-xs text-amber-600 mt-1">
                                  No assignments - selecting will have no effect
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {matches.length === 0 && !errorMessage && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No matching apps found in Intune for "{packageName}"</p>
                    <p className="text-xs mt-1">
                      {allApps.length > 0 
                        ? `Found ${allApps.length} app(s) total - expand below to select manually`
                        : "No apps found in your Intune tenant"}
                    </p>
                  </div>
                )}

                {/* Show all apps section */}
                <div className="border-t pt-4">
                  <button
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full"
                    onClick={() => setShowAllApps(!showAllApps)}
                  >
                    {showAllApps ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {showAllApps ? "Hide" : "Show"} all apps ({allApps.length})
                  </button>

                  {showAllApps && (
                    <div className="mt-3 space-y-3">
                      <Input
                        placeholder="Search all apps..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="max-w-sm"
                      />
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {filteredAllApps.map(app => (
                          <div
                            key={app.id}
                            className={`p-2 border rounded cursor-pointer text-sm ${
                              selectedApps.has(app.id)
                                ? "border-primary bg-primary/5"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => toggleAppSelection(app.id)}
                          >
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedApps.has(app.id)}
                                onCheckedChange={() => toggleAppSelection(app.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="truncate">{app.displayName}</span>
                              {app.displayVersion && (
                                <span className="text-xs text-muted-foreground font-mono">v{app.displayVersion}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Selection Summary */}
        {selectedApps.size > 0 && (
          <div className="bg-gray-50 -mx-6 px-6 py-3 border-t">
            <p className="text-sm">
              <strong>{selectedApps.size}</strong> app(s) selected
              {deploymentType === "test" 
                ? " - Test group will be excluded from these apps"
                : " - Assignments will be removed from these apps"
              }
            </p>
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={processing}
          >
            Skip
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={processing}
            className="bg-primary hover:bg-primary/90"
            data-testid="confirm-supersede-btn"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : selectedApps.size === 0 ? (
              "Continue Without Changes"
            ) : (
              <>
                {deploymentType === "test" ? (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Continue with Exclusion
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Assignments & Continue
                  </>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
